class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 800);

    const facetForm = this.querySelector('form');
    facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));
    document.getElementById('ProductGridContainer').classList.add('loading');
    if (countContainer) {
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some((element) => filterDataUrl(element))
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static async renderSectionFromFetch(url, event) {
    try {
      const response = await fetch(url);
      const html = await response.text();
      FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
      FacetFiltersForm.renderFilters(html, event);
      FacetFiltersForm.renderProductGridContainer(html);
      FacetFiltersForm.renderProductCount(html);
      if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
    } catch (error) {
      console.error('Error fetching facet filters:', error);
    }
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find((element) => filterDataUrl(element)).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
    if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
  }

  static renderProductGridContainer(html) {
    const productGridContainer = document.getElementById('ProductGridContainer');
    productGridContainer.innerHTML = new DOMParser()
      .parseFromString(html, 'text/html')
      .getElementById('ProductGridContainer').innerHTML;

    productGridContainer.classList.remove('loading');

    productGridContainer
      .querySelectorAll('.scroll-trigger')
      .forEach((element) => {
        element.classList.add('scroll-trigger--cancel');
      });
  }

  static renderProductCount(html) {
    const count = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductCount').innerHTML;
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    container.innerHTML = count;
    container.classList.remove('loading');
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );
    const facetDetailsElementsFromDom = document.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );

    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.remove();
      }
    });

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find((element) => matchesId(element));

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);
      if (currentElement) {
        document.getElementById(elementToRender.id).innerHTML = elementToRender.innerHTML;
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];
          if (elementToRender.className === previousElementClassName) {
            document.getElementById(previousElementId).after(elementToRender);
            return;
          }
        }

        if (elementToRender.parentElement) {
          document.querySelector(`#${elementToRender.parentElement.id} .js-filter`).before(elementToRender);
        }
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender) {
      const closestJSFilterID = event.target.closest('.js-filter').id;

      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
        FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(closestJSFilterID));

        const newFacetDetailsElement = document.getElementById(closestJSFilterID);
        const newElementSelector = newFacetDetailsElement.classList.contains('mobile-facets__details')
          ? `.mobile-facets__close-button`
          : `.facets__summary`;
        const newElementToActivate = newFacetDetailsElement.querySelector(newElementSelector);

        const isTextInput = event.target.getAttribute('type') === 'text';

        if (newElementToActivate && !isTextInput) newElementToActivate.focus();
      }
    }
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
    });

    document.getElementById('FacetFiltersFormMobile').closest('menu-drawer').bindEvents();
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    if (event.srcElement.className == 'mobile-facets__checkbox') {
      const searchParams = this.createSearchParams(event.target.closest('form'));
      this.onSubmitForm(searchParams, event);
    } else {
      const forms = [];
      const isMobile = event.target.closest('form').id === 'FacetFiltersFormMobile';

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm') {
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === 'FacetFiltersFormMobile') {
          forms.push(this.createSearchParams(form));
        }
      });
      this.onSubmitForm(forms.join('&'), event);
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  constructor() {
    super();

    this.facet = this.querySelector('.price-facet');
    if (!this.facet) {
      this.initLegacy();
      return;
    }

    this.rangeMax = this.parseNumber(this.facet.dataset.rangeMax) || 1000;

    this.slider = this.querySelector('.price-facet__slider');
    this.track = this.querySelector('.price-facet__track');
    this.trackFill = this.querySelector('.price-facet__track-fill');
    this.thumbMin = this.querySelector('.price-facet__thumb--min');
    this.thumbMax = this.querySelector('.price-facet__thumb--max');

    this.hiddenMin = this.querySelector('.price-facet__hidden--min');
    this.hiddenMax = this.querySelector('.price-facet__hidden--max');

    this.displayMin = this.querySelector('.price-facet__input--min-display');
    this.displayMax = this.querySelector('.price-facet__input--max-display');

    this.isDragging = false;
    this.currentThumb = null;

    this.initSlider();
    this.initDisplayInputs();
    this.updateUI();
  }

  parseNumber(str) {
    if (!str && str !== 0) return 0;
    return Number.parseFloat(String(str).replace(/\s/g, '').replace(',', '.')) || 0;
  }

  formatDisplay(num) {
    return num.toFixed(2).replace('.', ',');
  }

  getMinValue() {
    return this.parseNumber(this.hiddenMin?.value) || 0;
  }

  getMaxValue() {
    return this.parseNumber(this.hiddenMax?.value) || this.rangeMax;
  }

  initLegacy() {
    this.querySelectorAll('input').forEach((element) => {
      element.addEventListener('change', this.onLegacyChange.bind(this));
      element.addEventListener('keydown', this.onKeyDown.bind(this));
    });
    this.setMinAndMaxValues();
  }

  initSlider() {
    if (!this.slider) return;

    [this.thumbMin, this.thumbMax].forEach(thumb => {
      if (!thumb) return;
      thumb.addEventListener('mousedown', (e) => this.startDrag(e, thumb));
      thumb.addEventListener('touchstart', (e) => this.startDrag(e, thumb), { passive: false });
      thumb.addEventListener('keydown', (e) => this.onThumbKeyDown(e, thumb));
    });

    this.slider.addEventListener('click', (e) => this.onTrackClick(e));

    this._onDrag = (e) => this.onDrag(e);
    this._onEndDrag = () => this.endDrag();
    document.addEventListener('mousemove', this._onDrag);
    document.addEventListener('mouseup', this._onEndDrag);
    document.addEventListener('touchmove', this._onDrag, { passive: false });
    document.addEventListener('touchend', this._onEndDrag);
  }

  initDisplayInputs() {
    [this.displayMin, this.displayMax].forEach(input => {
      if (!input) return;

      input.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          input.blur();
        }
        this.onKeyDown(e);
      });

      input.addEventListener('blur', () => this.onDisplayInputChange());
    });
  }

  startDrag(e, thumb) {
    e.preventDefault();
    e.stopPropagation();
    this.isDragging = true;
    this.currentThumb = thumb;
    thumb.classList.add('dragging');
  }

  onDrag(e) {
    if (!this.isDragging || !this.slider) return;
    e.preventDefault();

    const rect = this.slider.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    let pct = (clientX - rect.left) / rect.width;
    pct = Math.max(0, Math.min(1, pct));

    const value = Math.round(pct * this.rangeMax);

    if (this.currentThumb === this.thumbMin) {
      const maxVal = this.getMaxValue();
      const clamped = Math.min(value, maxVal);
      if (this.hiddenMin) this.hiddenMin.value = clamped;
    } else if (this.currentThumb === this.thumbMax) {
      const minVal = this.getMinValue();
      const clamped = Math.max(value, minVal);
      if (this.hiddenMax) this.hiddenMax.value = clamped;
    }

    this.updateUI();
  }

  endDrag() {
    if (!this.isDragging) return;
    this.isDragging = false;
    this.currentThumb?.classList.remove('dragging');
    this.currentThumb = null;
    this.triggerFormUpdate();
  }

  onTrackClick(e) {
    if (this.isDragging || !this.slider) return;
    if (e.target.classList.contains('price-facet__thumb--min') ||
        e.target.classList.contains('price-facet__thumb--max')) return;

    const rect = this.slider.getBoundingClientRect();
    const pct = (e.clientX - rect.left) / rect.width;
    const value = Math.round(Math.max(0, Math.min(1, pct)) * this.rangeMax);

    const minVal = this.getMinValue();
    const maxVal = this.getMaxValue();

    if (Math.abs(value - minVal) <= Math.abs(value - maxVal)) {
      if (value <= maxVal && this.hiddenMin) this.hiddenMin.value = value;
    } else {
      if (value >= minVal && this.hiddenMax) this.hiddenMax.value = value;
    }

    this.updateUI();
    this.triggerFormUpdate();
  }

  onThumbKeyDown(e, thumb) {
    const step = e.shiftKey ? 10 : 1;
    let value = thumb === this.thumbMin ? this.getMinValue() : this.getMaxValue();

    switch (e.key) {
      case 'ArrowLeft':
      case 'ArrowDown':
        e.preventDefault();
        value = Math.max(0, value - step);
        break;
      case 'ArrowRight':
      case 'ArrowUp':
        e.preventDefault();
        value = Math.min(this.rangeMax, value + step);
        break;
      default:
        return;
    }

    if (thumb === this.thumbMin && this.hiddenMin) {
      this.hiddenMin.value = Math.min(value, this.getMaxValue());
    } else if (this.hiddenMax) {
      this.hiddenMax.value = Math.max(value, this.getMinValue());
    }

    this.updateUI();
    this.triggerFormUpdate();
  }

  onDisplayInputChange() {
    const minVal = this.parseNumber(this.displayMin?.value);
    const maxVal = this.parseNumber(this.displayMax?.value);

    const clampedMin = Math.max(0, Math.min(minVal, this.rangeMax));
    const clampedMax = Math.max(0, Math.min(maxVal, this.rangeMax));

    const finalMin = Math.min(clampedMin, clampedMax);
    const finalMax = Math.max(clampedMin, clampedMax);

    if (this.hiddenMin) this.hiddenMin.value = finalMin;
    if (this.hiddenMax) this.hiddenMax.value = finalMax;

    this.updateUI();
    this.triggerFormUpdate();
  }

  updateUI() {
    const minVal = this.getMinValue();
    const maxVal = this.getMaxValue();

    const minPct = (minVal / this.rangeMax) * 100;
    const maxPct = (maxVal / this.rangeMax) * 100;

    if (this.thumbMin) {
      this.thumbMin.style.left = `${minPct}%`;
      this.thumbMin.setAttribute('aria-valuenow', minVal);
    }
    if (this.thumbMax) {
      this.thumbMax.style.left = `${maxPct}%`;
      this.thumbMax.setAttribute('aria-valuenow', maxVal);
    }
    if (this.trackFill) {
      this.trackFill.style.left = `${minPct}%`;
      this.trackFill.style.right = `${100 - maxPct}%`;
    }

    if (this.displayMin) this.displayMin.value = this.formatDisplay(minVal);
    if (this.displayMax) this.displayMax.value = this.formatDisplay(maxVal);

    this.setMinAndMaxValues();
  }

  triggerFormUpdate() {
    if (this.hiddenMin) {
      this.hiddenMin.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  onLegacyChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  onKeyDown(event) {
    if (event.metaKey) return;
    const pattern = /[0-9]|\.|\,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input[name]');
    if (inputs.length < 2) return;

    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.dataset.max = maxInput.value;
    if (minInput.value) maxInput.dataset.min = minInput.value;
    if (minInput.value === '') maxInput.dataset.min = 0;
    if (maxInput.value === '') minInput.dataset.max = maxInput.dataset.max;
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.dataset.min);
    const max = Number(input.dataset.max);

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define('price-range', PriceRange);


class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);
